<template>
	<Modal v-model:visible="visible" :title="$t('settings.addUserModal.header')" @ok="onOk" @cancel="onCancel">
		<Form
			:model="formState"
			:label-col="{ span: 12 }"
			:wrapper-col="{ span: 12 }"
			:colon="false"
		>
			<FormItem
				:label="$t('settings.addUserModal.usernameLabel')"
				name="username"
				v-bind="validateInfos.username"
			>
				<InputRestricted
					ref="usernameField"
					v-model="formState.username"
					:allowedFormatChecker="alphaNumericChecker"
					:toolTipText="$t('validators.alphaNumericMessage')"
					@keyup.enter="onOk"
				/>
			</FormItem>
			<FormItem
				:label="$t('settings.addUserModal.roleLabel')"
				name="role"
				v-bind="validateInfos.role"
			>
				<Select v-model:value="formState.role" :options="roles" @keyup.enter="onOk" />
			</FormItem>
		</Form>
		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('settings.addUserModal.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" @click="onOk">{{ $t('settings.addUserModal.okButtonLabel') }}</Button>
		</template>
	</Modal>
</template>

<script>
import { reactive, ref, nextTick, toRaw, watch } from 'vue';
import { useVModel } from '@vueuse/core';

import { useI18n } from 'vue-i18n';
import { Modal, Form, FormItem, Select, Button, message } from 'ant-design-vue';

import Api from '@/api/Api';
import Validators from '@/api/Validators';

import { useUserStore } from '@/stores/user';
import { applyFieldErrors, runValidatedSubmit } from '@/components/common/formModal';

import InputRestricted from '@/components/common/InputRestricted';

export default {
	components: {
		Modal, Form, FormItem, Select, Button,
		InputRestricted,
	},
	props: {
		modelValue: Boolean,
		onAdd: Function,
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const visible = useVModel(props, 'modelValue', emit);
		const formState = reactive({
			username: '',
			role: '',
		});

		const {
			resetFields,
			validate,
			validateInfos,
		} = Validators.useAddUserFormRules(formState);

		const isSubmitting = ref(false);
		const usernameField = ref(null);
		const roles = useUserStore().visibleRoles;
		const alphaNumericChecker = Validators.alphaNumericChecker;

		watch(visible, (value) => {
			if (value) {
				// очищаем поля при открытии попапа
				resetFields();
				isSubmitting.value = false;
				nextTick(() => {
					if (usernameField.value?.focus) {
						usernameField.value.focus();
					}
				});
			}
		});

		const onOk = () => {
			runValidatedSubmit(validate, isSubmitting, () => {
				Api.profileCreate(toRaw(formState), response => {
					if (response.status === 'ok') {
						message.success(t('settings.addUserModal.messages.success'));
						visible.value = false;
						props.onAdd();
					} else {
						applyFieldErrors(validateInfos, response.errors, ['username', 'role']);
					}
					isSubmitting.value = false;
				});
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			visible,
			formState,
			validateInfos,
			isSubmitting,
			roles,
			alphaNumericChecker,
			usernameField,
			onOk,
			onCancel,
		};
	},
};
</script>
